<template>
  <div>
    <reports-filter
      ref="reportFilter"
      v-if="isVisible"
      is-company-field-required
      is-installation-field-required
      is-asset-field-required
      is-sensor-field-required
      has-sensor-field
      :displayFieldsInLine="displayFieldsInLine"
      :filter-objects.sync="reportsFilter"
      @user-companies-have-regional="regionalIsHidden = $event"
    >
      <b-col>
        <tag-options-input
          class="mb-3"
          is-required-field
          :label="$t('reports.network-status.fields.module-status')"
          :options-custom-label="$t('reports.filters.options.all-sensors-status')"
          :tags="sensorStatusOptions"
          :tag-label="'name'"
          :track-tag-by="'id'"
          :selected-items.sync="selectedFilter.moduleStatus"
        />
      </b-col>
      <b-col>
        <tag-options-input
          class="mb-3"
          is-required-field
          :label="$t('reports.network-status.fields.network-status')"
          :options-custom-label="$t('reports.filters.options.all-network-status')"
          :tag-label="'name'"
          :track-tag-by="'id'"
          :tags="networkStatusOptions"
          :selected-items.sync="selectedFilter.networkStatus"
        />
      </b-col>
      <b-col
        v-if="hasPER014()"
      >
        <tag-options-input
          class="mb-3"
          is-required-field
          :label="$t('reports.network-status.fields.module-self-diagnosis-status')"
          :options-custom-label="$t('reports.filters.options.all-module-self-diagnosis-status')"
          :tag-label="'name'"
          :track-tag-by="'id'"
          :tags="moduleSelfDiagnosisStatusOptions"
          :selected-items.sync="selectedFilter.moduleSelfDiagnosisStatuss"
        />
      </b-col>
      <b-col>
        <label>
          {{ $t('reports.network-status.fields.ip') }}
        </label>
        <ipv4-input
          class="mb-3"
          hide-validation
          v-model="selectedFilter.ip"
          :is-valid.sync="ipIsValid"
        />
      </b-col>
      <b-col>
        <label>
          {{ $t('reports.network-status.fields.address') }}
        </label>
        <s-number-input
          class="mb-3"
          v-model="selectedFilter.address"
          :numeric-options="numericOptions"
        />
      </b-col>
    </reports-filter>
    <b-row v-if="showPeriodField">
      <b-col>
        <period-date-select
          :initialDate.sync="selectedFilter.initialDate"
          :initialTime.sync="selectedFilter.initialTime"
          :finalDate.sync="selectedFilter.finalDate"
          :finalTime.sync="selectedFilter.finalTime"
          :period.sync="selectedFilter.period"
          :invalid.sync="periodInvalid"
          @update:initialDate="validatePeriodFields($event, 'initialDate')"
          @update:initialTime="validatePeriodFields($event, 'initialTime')"
          @update:finalDate="validatePeriodFields($event, 'finalDate')"
          @update:finalTime="validatePeriodFields($event, 'finalTime')"
          @update:initialDateTime="onUpdateInitialDateTime($event)"
          @update:finalDateTime="onUpdateFinalDateTime($event)"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import uniq from 'lodash/uniq';
import globalEventBus from '@/services/globalEventBus';
import networkStatusOptions from '@/domain/data/generalPanel/networkStatus/networkStatusOptions';
import sensorStatusOptions from '@/domain/data/generalPanel/networkStatus/moduleStatusOptions';
import moduleSelfDiagnosisStatusOptions from '@/domain/data/generalPanel/networkStatus/moduleSelfDiagnosisStatusOptions';
import ReportsFilter from '@/components/ReportsFilter';
import TagOptionsInput from '@/components/inputs/TagOptionsInput';
import periods from '@/domain/enums/periods';
import PeriodDateSelect from '@/components/PeriodDateSelect';
import Ipv4Input from '@/components/inputs/Ipv4Input';
import {
  fieldsToValidate,
  validations,
  validationsFields,
} from '@/domain/data/generalPanel/networkStatus/NetworkFilterValidations';
import { grammaticalJoin, mapArrayIds } from '@/services/arrayUtils';
import networkStatusEvents from '@/domain/events/networkStatus';

export default {
  name: 'NetworkFilter',
  components: {
    ReportsFilter,
    TagOptionsInput,
    PeriodDateSelect,
    Ipv4Input,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: true,
    },
    displayFieldsInLine: {
      type: Boolean,
      default: false,
    },
    showPeriodField: {
      type: Boolean,
      default: true,
    },
    isPeriodValid: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    filter: {
      companies: [],
      regional: [],
      installations: [],
      assets: [],
      sensors: [],
      networkStatus: [],
      moduleStatus: [],
      ip: '',
      address: null,
      moduleSelfDiagnosisStatuss: [],
    },
    reportsFilter: {
      companies: [],
      regional: [],
      installations: [],
      assets: [],
      sensors: [],
    },
    selectedFilter: {
      moduleStatus: sensorStatusOptions(),
      networkStatus: networkStatusOptions(),
      moduleSelfDiagnosisStatuss: moduleSelfDiagnosisStatusOptions(),
      period: periods.SEVEN,
      ip: '',
      address: null,
      initialDate: '',
      initialTime: '',
      initialDateTime: '',
      finalDate: '',
      finalTime: '',
      finalDateTime: '',
    },
    ipIsValid: true,
    periodInvalid: false,
    regionalIsHidden: true,
    sensorStatusOptions: sensorStatusOptions(),
    networkStatusOptions: networkStatusOptions(),
    moduleSelfDiagnosisStatusOptions: moduleSelfDiagnosisStatusOptions(),
    validationsFields,
    addressKey: 0,
    emptyFields: [],
  }),
  validations,
  computed: {
    numericOptions() {
      return {
        decimalPlaces: validationsFields.address.precision,
        minimumValue: validationsFields.address.minValue,
        maximumValue: validationsFields.address.maxValue,
      };
    },
    mappedFilter() {
      const filter = { ...this.mappedFilterObject };
      return {
        companiesIds: mapArrayIds(filter.companies),
        regionalsIds: mapArrayIds(filter.regional),
        installationsIds: mapArrayIds(filter.installations),
        assetsIds: mapArrayIds(filter.assets),
        modulesIds: mapArrayIds(filter.sensors),
        address: filter.address,
        ip: filter.ip,
        moduleStatus: mapArrayIds(filter.moduleStatus),
        networkStatus: mapArrayIds(filter.networkStatus),
        moduleSelfDiagnosisStatuss: mapArrayIds(filter.moduleSelfDiagnosisStatuss),
        period: filter.period,
        initialDate: filter.initialDateTime,
        finalDate: filter.finalDateTime,
      };
    },
    mappedFilterObject() {
      const {
        moduleSelfDiagnosisStatuss,
        initialDateTime,
        finalDateTime,
        period,
        ...filter
      } = this.selectedFilter;

      let emittedFilter = {
        ...this.reportsFilter,
        ...filter,
        moduleSelfDiagnosisStatuss: this.hasPER014() ? moduleSelfDiagnosisStatuss : null,
      };

      if (this.showPeriodField) {
        emittedFilter = {
          ...emittedFilter,
          period,
          initialDateTime,
          finalDateTime,
        };
      }

      return emittedFilter;
    },
    isPeriodsUndefined() {
      return (this.selectedFilter.period === periods.PERSONALIZED
        || this.selectedFilter.period === undefined);
    },
    isDateFilled() {
      const {
        initialDate,
        finalDate,
        initialTime,
        finalTime,
      } = this.selectedFilter;
      return Boolean(initialDate && finalDate && initialTime && finalTime);
    },
    isFilterPeriodValid() {
      return !(this.selectedFilter || {}).invalid
        && (this.isDateFilled || this.selectedFilter.period !== periods.PERSONALIZED);
    },
  },
  created() {
    this.initListener();
  },
  beforeDestroy() {
    this.removeListener();
  },
  methods: {
    initListener() {
      globalEventBus.on(networkStatusEvents.refreshFilter, this.onFilterChangedHandler);
      globalEventBus.on(networkStatusEvents.validateAndEmitFilters, this.validateAndEmitFilter);
    },
    removeListener() {
      globalEventBus.off(networkStatusEvents.refreshFilter, this.onFilterChangedHandler);
      globalEventBus.off(networkStatusEvents.validateAndEmitFilters, this.validateAndEmitFilter);
    },
    onFilterChangedHandler(newFilter, shouldCallValidateAndEmit = false) {
      this.filter = { ...newFilter };
      this.initializeFilter();

      if (shouldCallValidateAndEmit) {
        this.validateAndEmitFilter();
      }
    },
    onUpdateInitialDateTime(value) {
      this.selectedFilter.initialDateTime = value;
    },
    onUpdateFinalDateTime(value) {
      this.selectedFilter.finalDateTime = value;
    },
    setReportFilter() {
      const {
        companies,
        regional,
        installations,
        assets,
        sensors,
      } = this.filter;

      this.reportsFilter = {
        companies,
        regional,
        installations,
        assets,
        sensors,
      };

       this.$nextTick(() => {
        if (this.isVisible) {
          this.$refs.reportFilter.fillFilters();
        }
       });
    },
    setSelectedFieldsFilter() {
      const {
        networkStatus,
        ip,
        address,
        moduleSelfDiagnosisStatuss,
        moduleStatus,
      } = this.filter;

      const selectedModules = this.filterByList(
        this.sensorStatusOptions,
        moduleStatus,
      );

      const selectedNetworks = this.filterByList(
        this.networkStatusOptions,
        networkStatus,
      );

      const selectedSelfDiagnoses = this.filterByList(
        this.moduleSelfDiagnosisStatusOptions,
        moduleSelfDiagnosisStatuss,
      );

      this.selectedFilter = {
        ...this.selectedFilter,
        ip,
        address,
        moduleSelfDiagnosisStatuss: selectedSelfDiagnoses,
        moduleStatus: selectedModules,
        networkStatus: selectedNetworks,
      };

      this.addressKey += 1;
    },
    initializeFilter() {
      this.setSelectedFieldsFilter();
      this.setReportFilter();
    },
    reportErrors() {
      const invalidFields = fieldsToValidate
        .filter(({ filter, field }) => (filter
          ? this.$v[filter][field].$invalid
          : this.$v[field].$invalid)).map(({ label }) => this.$t(label));

      this.$pushAlert({
        code: 'MA001',
        messageParams: {
          NomeDoCampo: grammaticalJoin(invalidFields),
        },
      });
    },
    validateAndEmitFilter(event = 'on-filter-selected') {
      if (this.$v.$invalid) {
        this.reportErrors();
        return;
      }

      const eventData = {
        filter: this.mappedFilter,
        filterObject: this.mappedFilterObject,
      };

      this.$emit(event, eventData);
    },
    hasPER014() {
      return this.$hasUserPermission('PER014');
    },
    filterByList(list, filterList = []) {
      if (filterList.length) {
        return list.filter(item => filterList.map(x => (x.id || '').toUpperCase())
          .includes(item.id?.toUpperCase()));
      }

      return list;
    },
    validatePeriodFields(value, field) {
      if (value) {
        this.emptyFields = this.emptyFields.filter(item => item !== field);
      }
      if ((this.selectedFilter.period !== undefined && this.isPeriodsUndefined)
        && !value && !this.emptyFields.includes(field)) {
        this.emptyFields.push(field);
        this.emptyFields = uniq(this.emptyFields);
        this.showPeriodValidateMessage();
      }
    },
    showPeriodValidateMessage() {
      this.$pushAlert({
        code: 'MA001',
        messageParams: {
          NomeDoCampo: grammaticalJoin(this.emptyFields.map(
            field => this.getFieldLabel(field),
          )),
        },
      });
    },
    getFieldLabel(field) {
      const fields = {
        companiesIds: this.$t('entities.company'),
        installationsIds: this.$t('entities.installation'),
        assetsIds: this.$t('entities.asset'),
        modulesIds: this.$t('entities.module'),
        moduleStatus: this.$t('reports.network-status.fields.module-status'),
        networkStatus: this.$t('reports.network-status.fields.network-status'),
        finalDate: this.$t('fields.general.final-date'),
        finalTime: this.$t('fields.general.final-time'),
        initialTime: this.$t('fields.general.initial-time'),
        initialDate: this.$t('fields.general.initial-date'),
        moduleSelfDiagnosisStatuss: this.$t('reports.network-status.fields.module-self-diagnosis-status'),
        ip: this.$t('reports.network-status.fields.ip'),
        address: this.$t('reports.network-status.fields.address'),
      };
      return fields[field];
    },
  },
  watch: {
    isFilterPeriodValid(value) {
      this.$emit('update:isPeriodValid', value);
    },
  },
};
</script>
<style lang="sass" scoped>
.alert-warning-report
  &__period-select
    margin-bottom: 1rem
</style>
